/** Mic problems never fail silently: one help dialog for every record button, plus a start timeout. */
import { MicError, mapMicError } from "@/lib/micCheck";

const EVENT = "elpm:mic-help";

/** Open the Microphone help dialog for a MicError (or any thrown getUserMedia error). `retry` re-runs the action. */
export function showMicHelp(error, retry = null) {
  const e = error instanceof MicError ? error : mapMicError(error);
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { code: e.code, title: e.title, hint: e.hint, retry } }));
}

export function onMicHelp(fn) {
  const h = (ev) => fn(ev.detail);
  window.addEventListener(EVENT, h);
  return () => window.removeEventListener(EVENT, h);
}

export const micTimeoutError = () => new MicError("timeout", "The microphone didn't answer",
  "Your browser may be waiting for you to click Allow — look for a microphone prompt next to the address bar.");

/** engine.startRecording with a time limit; a start that lands after the limit is stopped so nothing records in secret. */
export function startRecordingWithTimeout(engine, channel, opts, ms = 25000) {
  let late = false;
  const run = engine.startRecording(channel, opts);
  run.then(() => { if (late) engine.stopRecording().catch(() => {}); }, () => {});
  const limit = new Promise((_, reject) => setTimeout(() => { late = true; reject(micTimeoutError()); }, ms));
  return Promise.race([run, limit]);
}

/** Current microphone permission: "granted" | "denied" | "prompt" | "unknown" (Safari / Firefox may not say). */
export async function micPermission() {
  try {
    const st = await navigator.permissions?.query?.({ name: "microphone" });
    return st?.state || "unknown";
  } catch { return "unknown"; }
}

const OS_STEPS = [
  "Windows: Start → Settings → Privacy & security → Microphone → turn on access for your browser.",
  "Mac: Apple menu → System Settings → Privacy & Security → Microphone → turn on your browser.",
];

/** Plain-language steps for each problem. */
export function micHelpSteps(code) {
  switch (code) {
    case "permission-denied":
    case "security-blocked":
      return ["Click the lock (or settings) icon to the left of the web address.", "Set Microphone to Allow.", "Reload the page and press Record again.", ...OS_STEPS];
    case "iframe-blocked":
      return ["Open the studio in its own browser tab (button below), then press Record again."];
    case "no-device":
      return ["Plug in your microphone or headset (USB mics: wait 5 seconds).", "Check it is the input chosen in your computer's sound settings.", "Press Try again."];
    case "device-busy":
      return ["Close Zoom, Teams, WhatsApp, Discord or any other tab using the mic.", "Unplug and replug a USB microphone.", "Press Try again."];
    case "timeout":
      return ["Look for a microphone prompt near the address bar and click Allow.", "If you don't see one, click the lock icon → Microphone → Allow, then reload.", ...OS_STEPS];
    case "insecure-context":
      return ["Open the studio from its https:// address."];
    case "unsupported-browser":
      return ["Use the latest Chrome, Edge or Firefox on a computer."];
    default:
      return ["Reload the page and try again.", "Make sure your microphone is plugged in and allowed for this site."];
  }
}
